/**
 * 개발 전용 목(mock) 모드 — UI 전환 작업용
 * ──────────────────────────────────────────────────────────────────
 * 백엔드(게이트웨이)가 떠 있지 않아도 인증이 필요한 화면의 레이아웃을 확인할 수
 * 있게 합니다. 디자인 전환 작업 중에만 쓰는 장치이고, 전환이 끝나면 삭제합니다.
 *
 * 켜는 법:  주소에 ?mock=1 을 붙이거나  localStorage.setItem('dokocrew_mock','1')
 * 끄는 법:  ?mock=0  또는 localStorage.removeItem('dokocrew_mock')
 *
 * 안전장치
 *  · REACT_APP_NODE_ENV === 'prod' 이면 어떤 경우에도 동작하지 않습니다.
 *  · 실제 서버 응답이 오면 그대로 사용하고, 네트워크 실패(연결 거부 등)일 때만
 *    픽스처로 대체합니다. 즉 백엔드가 살아 있으면 이 파일은 아무 일도 하지 않습니다.
 *  · 토큰은 형식만 맞춘 가짜입니다. 서버 검증을 통과하지 못하므로 목 모드 밖에서는
 *    401 을 받고 정상적으로 로그아웃됩니다.
 */

import config from 'config/config';

const FLAG_KEY = 'dokocrew_mock';

/** 목 모드 활성 여부 — prod 에서는 항상 false */
export const isMockMode = (): boolean => {
    if (config.NODE_ENV === 'prod') return false;
    try {
        const q = new URLSearchParams(window.location.search).get('mock');
        if (q === '1') localStorage.setItem(FLAG_KEY, '1');
        if (q === '0') localStorage.removeItem(FLAG_KEY);
        return localStorage.getItem(FLAG_KEY) === '1';
    } catch {
        return false;
    }
};

/** base64url 인코딩 (JWT payload 용) */
const b64u = (obj: unknown): string =>
    btoa(unescape(encodeURIComponent(JSON.stringify(obj))))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

/**
 * 형식이 유효한 가짜 access token.
 * axios 의 isAccessTokenExpired() 가 payload.exp 를 보므로 미래 시각을 넣습니다.
 * 서명은 검증하지 않는 클라이언트 로직(ProtectedRoute 등)만 통과합니다.
 */
const makeToken = (): string => {
    const header = b64u({ alg: 'HS256', typ: 'JWT' });
    const payload = b64u({
        sub: 'mock',
        userNo: 1,
        userId: 'mock@dokocrew.dev',
        nickName: '재권',
        exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    });
    return `${header}.${payload}.mock-signature`;
};

/** 목 모드일 때 로그인 상태를 흉내 냅니다. index.tsx 에서 최초 1회 호출. */
export const setupMockAuth = (): void => {
    if (!isMockMode()) return;
    try {
        localStorage.setItem('accessToken', makeToken());
        localStorage.setItem('refreshToken', 'mock-refresh');
        sessionStorage.setItem(
            'dokocrew_user_info',
            JSON.stringify({ userNo: 1, userId: 'mock@dokocrew.dev', nickName: '재권' }),
        );
        // eslint-disable-next-line no-console
        console.warn('[mock] 목 모드로 실행 중입니다. 실제 서버 응답이 없으면 픽스처를 씁니다.');
    } catch {
        /* storage 실패 무시 */
    }
};
